import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./AddProduct.css"; // reuse AddProduct styles
import { useTitle } from "../../hooks/useTitle";
import {
  DB_ID,
  PRODUCTS_TABLE,
  BUCKET_ID,
  tablesDB,
  storage,
} from "../../appwrite";
import { Permission, Role, ID, Query } from "appwrite";
import { ImagePlus, X, Star } from "lucide-react";

const CATEGORY_CODES = {
  "reup-vault": "RUV",
  "value-packs": "VLP",
  "fresh-farm-produce": "FFP",
  "grains-and-staples": "GNS",
  "meat-and-livestock": "MAL",
  catfish: "CTF",
  "dairy-and-eggs": "DAE",
  beverages: "BEV",
  "snacks-and-confectionery": "SNC",
  "canned-and-packaged-foods": "CPF",
  "frozen-foods": "FRF",
  "condiments-and-sauces": "CNS",
};

const generateSlug = (title) =>
  title
    .toString()
    .toLowerCase()
    .replace(/[^\w\s]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+$/g, "");

const storageUrl = (fileId) => `https://storage.trendbayds.com/f/${fileId}`;

export default function EditProduct() {
  useTitle("Edit Product - TrendBay Admin");
  const navigate = useNavigate();
  const { id } = useParams();

  const [product, setProduct] = useState({
    productName: "",
    productDescription: "",
    price: "",
    quantity: "",
    sku: "",
    category: "",
    available: true,
    featured: false,
    productCondition: "New",
  });

  // existing = { url: string }[], new = { file, preview }[]
  // We keep them in one unified array: { url?, file?, preview }
  // url-only = already uploaded; file = new to upload
  const [images, setImages] = useState([]); // { url?, file?, preview }
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const fileInputRef = useRef();

  const formatPrice = (v) => {
    const n = parseFloat(String(v).replace(/,/g, ""));
    if (isNaN(n)) return "";
    return n.toLocaleString("en-NG", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  };

  useEffect(() => {
    (async () => {
      try {
        const row = await tablesDB.getRow({
          databaseId: DB_ID,
          tableId: PRODUCTS_TABLE,
          rowId: id,
        });
        setProduct({
          productName: row.productName || "",
          productDescription: row.productDescription || "",
          price: formatPrice(row.price),
          quantity: row.quantity ?? "",
          sku: row.sku || "",
          category: row.category || "",
          available: row.available ?? true,
          featured: row.featured ?? false,
          productCondition: row.productCondition || "New",
        });

        // Build image list: cover first, then moreImages
        const existing = [];
        if (row.productImage)
          existing.push({ url: row.productImage, preview: row.productImage });
        (row.moreImages || []).forEach((u) =>
          existing.push({ url: u, preview: u }),
        );
        setImages(existing);
      } catch {
        toast.error("Failed to load product");
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const generateSKU = async (category) => {
    const code = CATEGORY_CODES[category];
    if (!code) return "";
    let sku, exists;
    do {
      const rand = Math.floor(100 + Math.random() * 900);
      sku = `${code}${rand}`;
      try {
        const res = await tablesDB.listRows({
          databaseId: DB_ID,
          tableId: PRODUCTS_TABLE,
          queries: [Query.equal("sku", sku)],
        });
        exists = (res.rows || []).length > 0;
      } catch {
        exists = false;
      }
    } while (exists);
    return sku;
  };

  const handleField = async (e) => {
    const { name, value, type, checked } = e.target;
    if (name === "category") {
      const sku = value ? await generateSKU(value) : product.sku;
      setProduct((p) => ({ ...p, category: value, sku }));
    } else if (name === "price") {
      setProduct((p) => ({ ...p, price: formatPrice(value) }));
    } else if (type === "checkbox") {
      setProduct((p) => ({ ...p, [name]: checked }));
    } else {
      setProduct((p) => ({ ...p, [name]: value }));
    }
  };

  const readFile = (file) =>
    new Promise((resolve) => {
      const r = new FileReader();
      r.onloadend = () => resolve(r.result);
      r.readAsDataURL(file);
    });

  const addImages = async (files) => {
    const entries = await Promise.all(
      Array.from(files).map(async (file) => ({
        file,
        preview: await readFile(file),
      })),
    );
    setImages((prev) => [...prev, ...entries]);
  };

  const removeImage = (idx) =>
    setImages((prev) => prev.filter((_, i) => i !== idx));

  const makeCover = (idx) => {
    setImages((prev) => {
      const next = [...prev];
      const [picked] = next.splice(idx, 1);
      return [picked, ...next];
    });
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    if (e.dataTransfer.files.length) await addImages(e.dataTransfer.files);
  };

  const validateFields = () =>
    product.productName &&
    product.productDescription &&
    product.price &&
    product.sku &&
    product.category &&
    images.length > 0;

  const handleSave = async () => {
    if (!validateFields()) {
      toast.error("Fill in all fields and keep at least one image.");
      return;
    }
    setSaving(true);
    try {
      // Upload new files, keep existing URLs as-is
      const urls = await Promise.all(
        images.map(async (img) => {
          if (!img.file) return img.url || null; // already uploaded, return stored URL
          const fileId = ID.unique();
          await storage.createFile(BUCKET_ID, fileId, img.file);
          return storageUrl(fileId);
        }),
      );

      const validUrls = urls.filter(Boolean);
      const [productImage, ...moreImages] = validUrls;

      await tablesDB.updateRow({
        databaseId: DB_ID,
        tableId: PRODUCTS_TABLE,
        rowId: id,
        data: {
          productName: product.productName,
          productDescription: product.productDescription,
          price: parseFloat(product.price.replace(/,/g, "")),
          quantity: parseInt(product.quantity || "0", 10),
          sku: product.sku,
          category: product.category,
          productImage,
          moreImages,
          available: product.available,
          featured: product.featured,
          productCondition: product.productCondition,
          slug: generateSlug(product.productName),
        },
      });

      toast.success("Product updated!");
      navigate("/d/all-products");
    } catch (e) {
      console.error(e);
      toast.error("Failed to update product.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="ap-page">
        <p style={{ color: "#888", fontFamily: "Inter, sans-serif" }}>
          Loading…
        </p>
      </div>
    );
  }

  return (
    <div className="ap-page">
      <div className="ap-header">
        <div>
          <h2 className="ap-title">Edit Product</h2>
          <p className="ap-subtitle">Update details and images</p>
        </div>
        <div className="ap-header-actions">
          <button
            className="ap-btn-ghost"
            onClick={() => navigate("/d/all-products")}
          >
            Discard
          </button>
          <button
            className="ap-btn-green"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? "Saving…" : "Save Changes"}
          </button>
        </div>
      </div>

      <div className="ap-body">
        {/* LEFT */}
        <div className="ap-main">
          {/* Images */}
          <div className="ap-card">
            <h3 className="ap-card-title">Product Images</h3>
            <p className="ap-card-hint">
              First image is the cover. Click ★ on any thumbnail to make it the
              cover.
            </p>

            <div
              className="ap-image-drop"
              onDrop={handleDrop}
              onDragOver={(e) => e.preventDefault()}
              onClick={() => fileInputRef.current.click()}
            >
              <ImagePlus size={28} color="#00A855" />
              <p>
                Drop images here or <span>click to browse</span>
              </p>
              <p className="ap-drop-hint">JPG, PNG, WEBP · Multiple allowed</p>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              style={{ display: "none" }}
              onChange={(e) => addImages(e.target.files)}
            />

            {images.length > 0 && (
              <div className="ap-image-grid">
                {images.map((img, idx) => (
                  <div
                    key={idx}
                    className={`ap-thumb ${idx === 0 ? "ap-thumb--cover" : ""}`}
                  >
                    <img src={img.preview} alt="" />
                    {idx === 0 && <span className="ap-thumb-badge">Cover</span>}
                    <div className="ap-thumb-actions">
                      {idx !== 0 && (
                        <button
                          className="ap-thumb-btn ap-thumb-star"
                          onClick={() => makeCover(idx)}
                          title="Make cover"
                        >
                          <Star size={13} />
                        </button>
                      )}
                      <button
                        className="ap-thumb-btn ap-thumb-remove"
                        onClick={() => removeImage(idx)}
                        title="Remove"
                      >
                        <X size={13} />
                      </button>
                    </div>
                  </div>
                ))}
                <div
                  className="ap-thumb ap-thumb--add"
                  onClick={() => fileInputRef.current.click()}
                >
                  <ImagePlus size={22} color="#aaa" />
                  <span>Add more</span>
                </div>
              </div>
            )}
          </div>

          {/* Info */}
          <div className="ap-card">
            <h3 className="ap-card-title">Product Information</h3>
            <div className="ap-field">
              <label className="ap-label">
                Title <span className="ap-req">*</span>
              </label>
              <input
                className="ap-input"
                type="text"
                name="productName"
                value={product.productName}
                onChange={handleField}
                placeholder="e.g. Fresh Tomatoes 5kg"
              />
            </div>
            <div className="ap-field">
              <label
                className="ap-label"
                style={{ display: "flex", justifyContent: "space-between" }}
              >
                <span>
                  Description <span className="ap-req">*</span>
                </span>
                <span
                  style={{
                    fontWeight: 400,
                    color:
                      product.productDescription.length > 950
                        ? "#e74c3c"
                        : "#bbb",
                  }}
                >
                  {product.productDescription.length}/1000
                </span>
              </label>
              <textarea
                className="ap-input ap-textarea"
                name="productDescription"
                value={product.productDescription}
                onChange={handleField}
                rows={6}
                maxLength={1000}
                placeholder="Describe the product - quality, size, origin, storage, etc."
              />
            </div>
          </div>
        </div>

        {/* RIGHT */}
        <div className="ap-side">
          <div className="ap-card">
            <h3 className="ap-card-title">Pricing & Details</h3>

            <div className="ap-price-qty-row">
              <div className="ap-field ap-price-col">
                <label className="ap-label">
                  Price (NGN) <span className="ap-req">*</span>
                </label>
                <div className="ap-price-wrap">
                  <span className="ap-currency">₦</span>
                  <input
                    className="ap-input ap-input--price"
                    type="text"
                    name="price"
                    value={product.price}
                    onChange={handleField}
                    placeholder="0"
                  />
                </div>
              </div>
              <div className="ap-field ap-qty-col">
                <label className="ap-label">Qty</label>
                <input
                  className="ap-input"
                  type="number"
                  name="quantity"
                  value={product.quantity}
                  onChange={handleField}
                  placeholder="0"
                  min="0"
                />
              </div>
            </div>

            <div className="ap-field">
              <label className="ap-label">
                Category <span className="ap-req">*</span>
              </label>
              <select
                className="ap-input"
                name="category"
                value={product.category}
                onChange={handleField}
              >
                <option value="">Select a category</option>
                <option value="reup-vault">ReUp Vault</option>
                <option value="value-packs">Value Packs</option>
                <option value="fresh-farm-produce">Fresh Farm Produce</option>
                <option value="grains-and-staples">Grains and Staples</option>
                <option value="meat-and-livestock">Meat and Livestock</option>
                <option value="catfish">Catfish</option>
                <option value="dairy-and-eggs">Dairy and Eggs</option>
                <option value="beverages">Beverages</option>
                <option value="snacks-and-confectionery">
                  Snacks and Confectionery
                </option>
                <option value="canned-and-packaged-foods">
                  Canned and Packaged Foods
                </option>
                <option value="frozen-foods">Frozen Foods</option>
                <option value="condiments-and-sauces">
                  Condiments and Sauces
                </option>
              </select>
            </div>

            <div className="ap-field">
              <label className="ap-label">SKU</label>
              <input
                className="ap-input ap-input--muted"
                type="text"
                name="sku"
                value={product.sku}
                readOnly
              />
            </div>

            <div className="ap-field">
              <label className="ap-label">Condition</label>
              <select
                className="ap-input"
                name="productCondition"
                value={product.productCondition}
                onChange={handleField}
              >
                <option value="New">New</option>
                <option value="Used">Used</option>
                <option value="Refurbished">Refurbished</option>
              </select>
            </div>
          </div>

          <div className="ap-card">
            <h3 className="ap-card-title">Visibility</h3>
            <div className="ap-toggle-row">
              <div>
                <p className="ap-toggle-label">Available for sale</p>
                <p className="ap-toggle-hint">Visible to customers</p>
              </div>
              <label className="ap-switch">
                <input
                  type="checkbox"
                  name="available"
                  checked={product.available}
                  onChange={handleField}
                />
                <span className="ap-switch-track" />
              </label>
            </div>
            <div className="ap-toggle-row">
              <div>
                <p className="ap-toggle-label">Featured</p>
                <p className="ap-toggle-hint">Show on homepage</p>
              </div>
              <label className="ap-switch">
                <input
                  type="checkbox"
                  name="featured"
                  checked={product.featured}
                  onChange={handleField}
                />
                <span className="ap-switch-track" />
              </label>
            </div>
          </div>

          <div className="ap-side-actions">
            <button
              className="ap-btn-green ap-btn-full"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? "Saving…" : "Save Changes"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
